import { useMemo } from 'react';
import { cn } from '../../../lib/utils';
import {
  compareRowsBySeverity,
  derivePanelAvailability,
  type ExternalIntelContext,
  type ListCompositesResponse,
  type PanelAvailability,
} from '../../../api/intelClient';
import { CompositeRow } from './CompositeRow';

interface ExternalIntelligencePanelProps {
  /** The hook payload — `null` for the silent-skip / 401+503 path. */
  data: ListCompositesResponse | null | undefined;
  /** True while TanStack Query is mid-fetch on first load. */
  isLoading: boolean;
  /** Hard error (network, 5xx other than 503). 401+503 are pre-handled
   * upstream — the fetcher returns `{ data: null, silentSkip: true }`
   * rather than throwing, so we never see them as errors here. */
  error: unknown;
  /** Operator context — drives the audit-skip beacon in sprint-3
   * (3n8j.3). Currently unused inside the panel but accepted so the
   * mount surface can keep a single source of truth for context. */
  context: ExternalIntelContext | null;
  className?: string;
}

/**
 * External Intelligence panel — cycle-105 sprint-1 Task 3 (FR-1.2 / 3n8j.1).
 *
 * Walled-off rail for WorldMonitor composite signals. Visually distinct from
 * Echelon-scored content via the `EXTERNAL · UNVERIFIED BY ECHELON`
 * badge in the header — the panel's single chromatic element (iris).
 * Bands stay achromatic per the brief's structural commitment.
 *
 * Pure-on-data — sibling pattern to `EIIInspectorPanel`. The page-level
 * mount (inline `ExternalIntelligenceRail` in `InvestigationWorkspaceP-
 * ageV2`) feeds the hook payload in.
 *
 * Mount-side responsibilities (kept OUT of this component):
 *  - `data-echelon-scored="false"` attribute on the wrapping element
 *  - audit-skip beacon firing on silent-skip (sprint-3 / 3n8j.3)
 *  - CSP middleware (sprint-2 / 3n8j.2)
 */
export function ExternalIntelligencePanel({
  data,
  isLoading,
  error,
  className,
}: ExternalIntelligencePanelProps) {
  // ── Silent-skip — feature flag off, or unauthenticated ────────────
  // The hook's `silentSkip` path returns `data: null`. We render no DOM
  // so the surface is invisible per FR-1.5.c. The mount-site wrapper
  // owns the audit-skip beacon (sprint-3).
  if (data === null && !isLoading && !error) {
    return null;
  }

  // ── Hard error — render an unobtrusive failure shell ──────────────
  // Distinct from silent-skip: the operator gets a small "external
  // intelligence unavailable" chip so they know the panel WAS supposed
  // to be there. Sprint-2 expands this; sprint-1 scaffolds.
  if (error) {
    return (
      <PanelShell className={className} availability="unavailable">
        <p
          role="alert"
          className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] px-2 py-3"
        >
          external intelligence unavailable
        </p>
      </PanelShell>
    );
  }

  // ── Loading (first fetch only — subsequent refetches keep stale data) ─
  if (isLoading && !data) {
    return (
      <PanelShell className={className} availability="unavailable">
        <p
          role="status"
          aria-live="polite"
          className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] px-2 py-3"
        >
          fetching external intelligence…
        </p>
      </PanelShell>
    );
  }

  if (!data) return null;

  return <PanelBody data={data} className={className} />;
}

/** Sort rows by severity descending so the operator's eye lands on
 * `Critical` first. Memoised — the row list churns whenever the hook
 * refetches but the input order is stable enough for cheap re-sort. */
function PanelBody({
  data,
  className,
}: {
  data: ListCompositesResponse;
  className?: string;
}) {
  const sortedRows = useMemo(
    () => [...data.composites].sort(compareRowsBySeverity),
    [data.composites],
  );
  const availability = useMemo(
    () => derivePanelAvailability(data.composites),
    [data.composites],
  );

  return (
    <PanelShell
      className={className}
      availability={availability}
      contextFilterApplied={data.panel_metadata.context_filter_applied}
      rowCount={sortedRows.length}
    >
      {sortedRows.length === 0 ? (
        <p
          role="status"
          className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] px-2 py-3"
        >
          no external intelligence for this context
        </p>
      ) : (
        <ul
          aria-label="External intelligence composite rows"
          className="flex flex-col"
        >
          {sortedRows.map((row) => (
            <CompositeRow
              key={`${row.composite_type}|${row.region ?? '_'}`}
              row={row}
            />
          ))}
        </ul>
      )}
    </PanelShell>
  );
}

// ─── PanelShell (inline, sibling to EII/Convergence/FocalPoint/Calibr) ──

interface PanelShellProps {
  availability: PanelAvailability;
  contextFilterApplied?: boolean;
  rowCount?: number;
  className?: string;
  children: React.ReactNode;
}

/** Inline panel shell — mirrors the EII/Convergence inspector shells in
 * this directory. Centralised here only by convention, not exported:
 * each intel inspector keeps its own shell for now. A future refactor
 * can extract a shared `IntelPanelShell` once the 5 inspectors prove
 * the pattern (NOTES.md Decision Log 2026-05-18). */
function PanelShell({
  availability,
  contextFilterApplied,
  rowCount,
  className,
  children,
}: PanelShellProps) {
  return (
    <section
      aria-label="External intelligence"
      data-echelon-scored="false"
      data-testid="external-intel-panel"
      data-availability={availability}
      data-context-filter-applied={contextFilterApplied ? 'true' : 'false'}
      className={cn(
        'flex flex-col gap-2',
        'rounded-lg border border-[var(--e-border-secondary)]',
        'bg-[var(--e-bg-card)]/85 backdrop-blur-md',
        'p-4',
        className,
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
            External intelligence
          </span>
          {/* Context-scope hint — load-bearing per brief Render rules 1.
              When `context_filter_applied=false`, the panel is showing
              network-wide data, NOT scoped to the current investigation.
              The operator must read this before reading the rows. */}
          {contextFilterApplied === false && (
            <span
              className="text-[10px] font-mono italic text-[var(--e-text-muted)]/80"
              data-testid="external-intel-context-scope-hint"
            >
              (network-wide; not investigation-scoped)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {typeof rowCount === 'number' && (
            <span
              className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]"
              data-testid="external-intel-row-count"
            >
              {rowCount} {rowCount === 1 ? 'row' : 'rows'}
            </span>
          )}
          <ExternalBadge />
        </div>
      </header>
      {children}
    </section>
  );
}

/** EXTERNAL · UNVERIFIED BY ECHELON — the single chromatic element on
 * the panel. Iris (`--cd-sig-iris`) is the design system's reserved
 * signal hue for "external/non-canonical". Property 4 (Color is
 * information): chroma here means "this came from outside Echelon's
 * verification surface — do not anchor to it like you would EII." */
function ExternalBadge() {
  return (
    <span
      data-testid="external-intel-unverified-badge"
      role="note"
      aria-label="External intelligence not verified by Echelon"
      className={cn(
        'inline-flex items-center px-2 py-0.5',
        'rounded-sm text-[9px] font-mono uppercase tracking-[0.12em]',
        'border border-[var(--cd-sig-iris)]/60',
        'text-[var(--cd-sig-iris)]',
        'bg-[var(--cd-sig-iris)]/[0.06]',
      )}
    >
      External · Unverified by Echelon
    </span>
  );
}
